import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { AvatarModule } from 'primeng/avatar';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';
import { UserService, User } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { MenuModule } from 'primeng/menu';
import { AccordionModule } from 'primeng/accordion';
import { environment } from '../../../../environments/environment';
import { SETTINGS } from '../../../core/config/common.settings';
import { LanguageService } from '../../../core/services/language.service';
import { FileIconService, FileIconInfo } from '../../../core/services/file-icon.service';
@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    AvatarModule,
    ToastModule,
    ConfirmDialogModule,
    MenuModule,
    AccordionModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './user-profile.html',
  styleUrl: './user-profile.scss'
})
export class UserProfile implements OnInit {
  activeTab: 'details' | 'password' | 'products' | 'territories' | 'settings' = 'details';
  currentUser: User | null = null;
  loading = false;
  updatingPassword = false;
  navigationItems: MenuItem[] | undefined;
  activeItem: any;
  // Password form
  passwordForm: FormGroup;
  
  // Edit mode states
  editingPhone = false;
  editingAddress = false;
  updatingPhone = false;
  updatingAddress = false;
  
  // Phone and address forms
  phoneForm: FormGroup;
  addressForm: FormGroup;
  
  // Language settings - get from language service
  availableLanguages: { code: string; name: string; flag: string }[] = [];
  selectedLanguage: string = 'en';

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private languageService: LanguageService,
    private router: Router,
    private route: ActivatedRoute,
    private fileIconService: FileIconService
  ) {
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required, 
        Validators.minLength(SETTINGS.PASSWORD.MIN_LENGTH),
        Validators.pattern(SETTINGS.PASSWORD.VALIDATION_REGEX)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
    
    // Phone form
    this.phoneForm = this.fb.group({
      mobileNumber: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]]
    });
    
    // Address form
    this.addressForm = this.fb.group({
      streetAddress: [''],
      houseNumber: [''],
      postcode: [''],
      city: [''],
      country: ['']
    });
  }

  ngOnInit() {
    // Check for tab query parameter
    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        const tab = params['tab'] as 'details' | 'password' | 'products' | 'territories' | 'settings';
        if (['details', 'password', 'products', 'territories', 'settings'].includes(tab)) {
          this.activeTab = tab;
          this.buildNavigationItems(); // Rebuild navigation to update active states
        }
      }
    });
    
    // Initialize available languages from service
    this.availableLanguages = this.languageService.getAvailableLanguages();
    
    this.loadCurrentUser();
    this.buildNavigationItems();
    this.loadSelectedLanguage();
  }
  
  loadSelectedLanguage() {
    // First check if user has a saved language preference
    if (this.currentUser && (this.currentUser as any).language) {
      this.selectedLanguage = (this.currentUser as any).language;
    } else {
      // Fallback to subscription language or localStorage
      this.selectedLanguage = this.languageService.getCurrentLang();
    }
  }
  
  changeLanguage(lang: string) {
    if (this.languageService.isValidLanguage(lang) && lang !== this.selectedLanguage) {
      // Ask for confirmation before changing language
      this.confirmationService.confirm({
        message: 'Changing the language will reload the application and you will need to log in again. Do you want to continue?',
        header: 'Confirm Language Change',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Change Language',
        rejectLabel: 'Cancel',
        acceptButtonStyleClass: 'p-button-primary',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          // Save to backend first
          this.userService.updateCurrentUserProfile({ language: lang } as any).subscribe({
            next: () => {
              // Save to localStorage
              this.languageService.setLanguage(lang);
              this.selectedLanguage = lang;
              
              this.messageService.add({
                severity: 'info',
                summary: 'Changing Language',
                detail: `Switching to ${this.availableLanguages.find(l => l.code === lang)?.name || lang}... You will be redirected to the login page.`,
                life: 3000
              });
              
              // Reload the application with new language after a short delay
              setTimeout(() => {
                window.location.href = `/${lang}/`;
              }, 1000);
            },
            error: (error) => {
              console.error('Error saving language preference:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to save language preference. Please try again.',
                life: 3000
              });
            }
          });
        },
        reject: () => {
          // User cancelled - no action needed
          this.messageService.add({
            severity: 'info',
            summary: 'Cancelled',
            detail: 'Language change cancelled',
            life: 2000
          });
        }
      });
    }
  }

  buildNavigationItems() {
    const baseItems = [
      { 
        label: 'My Details', 
        icon: 'pi pi-user', 
        styleClass: this.activeTab === 'details' ? 'active-menu-item' : '',
        command: () => this.setActiveTab('details') 
      }
    ];

    // Only show Products and Territories tabs for roles other than CSM and SM
    const userRole = this.currentUser?.roleId?.identification;
    const shouldShowProductsAndTerritories = userRole !== 'CSM' && userRole !== 'SM' && userRole !== 'SA' && userRole !== 'A' && userRole !== 'SUPER_DEV_ADMIN';

    if (shouldShowProductsAndTerritories) {
      baseItems.push(
        { 
          label: 'Products', 
          icon: 'pi pi-box', 
          styleClass: this.activeTab === 'products' ? 'active-menu-item' : '', 
          command: () => this.setActiveTab('products') 
        },
        { 
          label: 'Territories', 
          icon: 'pi pi-map', 
          styleClass: this.activeTab === 'territories' ? 'active-menu-item' : '', 
          command: () => this.setActiveTab('territories') 
        }
      );
    }

    baseItems.push(
      { 
        label: 'Password', 
        icon: 'pi pi-lock', 
        styleClass: this.activeTab === 'password' ? 'active-menu-item' : '', 
        command: () => this.setActiveTab('password') 
      },
      { 
        label: 'Settings', 
        icon: 'pi pi-cog', 
        styleClass: this.activeTab === 'settings' ? 'active-menu-item' : '', 
        command: () => this.setActiveTab('settings') 
      },
      { 
        label: 'Logout', 
        icon: 'pi pi-sign-out',
        styleClass: '',
        command: () => this.logout() 
      }
    );

    this.navigationItems = baseItems;
  }

  loadCurrentUser() {
    this.loading = true;
    // Use the new service method to get current user profile
    this.userService.getCurrentUserProfile().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.loading = false;
        // Rebuild navigation items after user data is loaded
        this.buildNavigationItems();
        // Initialize forms with current values
        this.initializeForms();
        // Load language preference from user profile
        this.loadSelectedLanguage();
      },
      error: (error) => {
        console.error('Error loading user profile:', error);
        this.loading = false;
        
        let errorMessage = 'Failed to load user profile';
        if (error.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
          // Redirect to login after a delay
          setTimeout(() => {
            this.logout();
          }, 2000);
        } else if (error.status === 0) {
          errorMessage = 'Network error. Please check your connection.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
      }
    });
  }
  
  initializeForms() {
    if (this.currentUser) {
      // Initialize phone form
      this.phoneForm.patchValue({
        mobileNumber: this.currentUser.mobileNumber || ''
      });
      
      // Initialize address form
      this.addressForm.patchValue({
        streetAddress: this.currentUser.address?.streetAddress || '',
        houseNumber: this.currentUser.address?.houseNumber || '',
        postcode: this.currentUser.address?.postcode || '',
        city: this.currentUser.address?.city || '',
        country: this.currentUser.address?.country || ''
      });
    }
  }
  
  // Phone number edit methods
  startEditingPhone() {
    this.editingPhone = true;
    this.initializeForms();
  }
  
  cancelEditingPhone() {
    this.editingPhone = false;
    this.initializeForms(); // Reset to original values
  }
  
  savePhone() {
    if (this.phoneForm.valid) {
      this.updatingPhone = true;
      const phoneData = {
        mobileNumber: this.phoneForm.value.mobileNumber || null
      };
      
      this.userService.updateCurrentUserProfile(phoneData).subscribe({
        next: (response) => {
          this.updatingPhone = false;
          this.editingPhone = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Phone number updated successfully'
          });
          this.refreshUserProfile();
        },
        error: (error) => {
          this.updatingPhone = false;
          console.error('Error updating phone number:', error);
          let errorMessage = 'Failed to update phone number';
          if (error.error?.message) {
            errorMessage = error.error.message;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
    } else {
      this.markFormGroupTouched(this.phoneForm);
    }
  }
  
  // Address edit methods
  startEditingAddress() {
    this.editingAddress = true;
    this.initializeForms();
  }
  
  cancelEditingAddress() {
    this.editingAddress = false;
    this.initializeForms(); // Reset to original values
  }
  
  saveAddress() {
    if (this.addressForm.valid) {
      this.updatingAddress = true;
      const addressData = {
        address: {
          streetAddress: this.addressForm.value.streetAddress || null,
          houseNumber: this.addressForm.value.houseNumber || null,
          postcode: this.addressForm.value.postcode || null,
          city: this.addressForm.value.city || null,
          country: this.addressForm.value.country || null
        }
      };
      
      this.userService.updateCurrentUserProfile(addressData).subscribe({
        next: (response) => {
          this.updatingAddress = false;
          this.editingAddress = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Address updated successfully'
          });
          this.refreshUserProfile();
        },
        error: (error) => {
          this.updatingAddress = false;
          console.error('Error updating address:', error);
          let errorMessage = 'Failed to update address';
          if (error.error?.message) {
            errorMessage = error.error.message;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
    } else {
      this.markFormGroupTouched(this.addressForm);
    }
  }

  setActiveTab(tab: 'details' | 'password' | 'products' | 'territories' | 'settings') {
    this.activeTab = tab;
    this.buildNavigationItems(); // Rebuild navigation to update active states
  }

  onPasswordSubmit() {
    if (this.passwordForm.valid) {
      this.updatingPassword = true;
      const { currentPassword, newPassword } = this.passwordForm.value;
      
      // Use the new service method to change password
      this.userService.changeCurrentUserPassword({
        currentPassword,
        newPassword
      }).subscribe({
        next: (response) => {
          this.updatingPassword = false;
          this.onPasswordUpdateSuccess();
        },
        error: (error) => {
          this.updatingPassword = false;
          this.onPasswordUpdateError(error);
        }
      });
    } else {
      this.markFormGroupTouched(this.passwordForm);
    }
  }

  passwordMatchValidator(group: FormGroup) {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.passwordForm.get(fieldName);
    if (field?.errors && field?.touched) {
      if (field.errors['required']) return SETTINGS.PASSWORD_ERROR_MESSAGES.REQUIRED;
      if (field.errors['minlength']) return `Password must be at least ${SETTINGS.PASSWORD.MIN_LENGTH} characters`;
      if (field.errors['pattern']) return SETTINGS.PASSWORD_ERROR_MESSAGES.INVALID_FORMAT;
    }
    return '';
  }

  getPasswordMismatchError(): string {
    if (this.passwordForm.errors?.['passwordMismatch'] && 
        this.passwordForm.get('confirmPassword')?.touched) {
      return 'Passwords do not match';
    }
    return '';
  }

  logout() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to logout? You will need to sign in again to access your account.',
      header: 'Confirm Logout',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-sign-out',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Yes, Logout',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Logging out',
          detail: 'You have been logged out successfully'
        });
        // Small delay to show the message before logout
        setTimeout(() => {
          this.authService.logout();
        }, 1000);
      },
      reject: () => {
        // User cancelled logout - no action needed
        console.log('Logout cancelled by user');
      }
    });
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  // Change current user's password
  changeCurrentUserPassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }) {
    return this.userService.changeCurrentUserPassword(passwordData);
  }

  // Check profile completion status
  checkProfileCompletion() {
    return this.userService.checkProfileCompletion();
  }

  // Refresh user profile data
  refreshUserProfile() {
    if (this.loading) return; // Prevent multiple simultaneous requests
    
    this.loading = true;
    this.userService.getCurrentUserProfile().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.loading = false;
        // Reinitialize forms with updated values
        this.initializeForms();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Profile refreshed successfully'
        });
      },
      error: (error) => {
        this.loading = false;
        console.error('Error refreshing user profile:', error);
        
        let errorMessage = 'Failed to refresh profile';
        if (error.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
          setTimeout(() => {
            this.logout();
          }, 2000);
        } else if (error.status === 0) {
          errorMessage = 'Network error. Please check your connection.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
      }
    });
  }

  // Handle password update success
  onPasswordUpdateSuccess() {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Password updated successfully'
    });
    this.passwordForm.reset();
    // Refresh user profile to ensure data is up to date
    this.refreshUserProfile();
  }

  // Handle password update error
  onPasswordUpdateError(error: any) {
    console.error('Error updating password:', error);
    let errorMessage = 'Failed to update password';
    
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'Current password is incorrect';
    } else if (error.status === 400) {
      errorMessage = 'Password does not meet requirements';
    }
    
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage
    });
  }

  // Get profile completion percentage
  getProfileCompletionPercentage(): number {
    if (!this.currentUser) return 0;
    
    let completedFields = 0;
    const totalFields = 8; // Basic required fields
    
    if (this.currentUser.firstName) completedFields++;
    if (this.currentUser.lastName) completedFields++;
    if (this.currentUser.email) completedFields++;
    if (this.currentUser.mobileNumber) completedFields++;
    if (this.currentUser.address?.streetAddress) completedFields++;
    if (this.currentUser.nationalInsuranceNo) completedFields++;
    if (this.currentUser.taxNumber) completedFields++;
    if (this.currentUser.idCard) completedFields++;
    
    return Math.round((completedFields / totalFields) * 100);
  }

  // Check if profile is complete
  isProfileComplete(): boolean {
    return this.getProfileCompletionPercentage() >= 80;
  }

  // Get password strength
  getPasswordStrength(password: string): { score: number; label: string; color: string } {
    if (!password) return { score: 0, label: 'Enter password', color: 'bg-gray-300' };
    
    let score = 0;
    if (password.length >= SETTINGS.PASSWORD.MIN_LENGTH) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  }

  // Get current password strength
  getCurrentPasswordStrength() {
    const newPassword = this.passwordForm.get('newPassword')?.value;
    return this.getPasswordStrength(newPassword);
  }

  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  downloadIdCard(idCardPath: string) {
    if (!idCardPath) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No ID card available for download'
      });
      return;
    }

    // Check if it's already a full URL (AWS S3)
    if (idCardPath.startsWith('http')) {
      // It's already a full URL, open in new tab
      window.open(idCardPath, '_blank');
    } else {
      // It's a relative path, construct the full URL
      const fullUrl = `${environment.apiUrl}/files/${idCardPath}`;
      window.open(fullUrl, '_blank');
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'ID card download started'
    });
  }

  // Get current user's products for display
  getCurrentUserProducts(): Array<{_id: string, name: string, code: string}> {
    if (!this.currentUser?.products) return [];
    
    return this.currentUser.products.map((p: any) => {
      if (typeof p === 'string') {
        // If it's just a string ID, we can't show product details
        return { _id: p, name: 'Unknown Product', code: 'N/A' };
      }
      // If it's a populated object, use the actual data
      return {
        _id: p._id,
        name: p.name || 'Unknown Product',
        code: p.code || 'N/A'
      };
    });
  }

  // Get current user's territories grouped by region for display
  getCurrentUserTerritoriesGrouped(): {[region: string]: Array<{_id: string, name: string}>} {
    if (!this.currentUser?.territories) return {};
    
    const grouped: {[region: string]: Array<{_id: string, name: string}>} = {};
    
    this.currentUser.territories.forEach((t: any) => {
      let territory;
      if (typeof t === 'string') {
        // If it's just a string ID, we can't show region info
        territory = { _id: t, name: 'Unknown Territory', region: 'Unknown Region' };
      } else {
        // If it's a populated object, use the actual data
        territory = {
          _id: t._id,
          name: t.name || 'Unknown Territory',
          region: t.region || 'Unknown Region'
        };
      }
      
      if (!grouped[territory.region]) {
        grouped[territory.region] = [];
      }
      grouped[territory.region].push({
        _id: territory._id,
        name: territory.name
      });
    });
    
    return grouped;
  }

  // Get regions for current user territories
  getCurrentUserTerritoryRegions(): string[] {
    return Object.keys(this.getCurrentUserTerritoriesGrouped());
  }

  // Get territories for a specific region for current user
  getCurrentUserTerritoriesForRegion(region: string): Array<{_id: string, name: string}> {
    return this.getCurrentUserTerritoriesGrouped()[region] || [];
  }

  // Get role-based color for role badges
  getRoleBasedColor(role: string): string {
    switch (role) {
      case 'CSM':
        return 'bg-purple-600';
      case 'SM':
        return 'bg-blue-600';
      case 'SA':
        return 'bg-green-600';
      case 'A':
        return 'bg-red-600';
      case 'FSR':
        return 'bg-yellow-600';
      case 'SUPER_DEV_ADMIN':
        return 'bg-indigo-600';
      default:
        return 'bg-gray-600';
    }
  }

  // Get full role name for display
  getRoleName(role: string): string {
    switch (role) {
      case 'CSM':
        return 'Customer Success Manager';
      case 'SM':
        return 'Sales Manager';
      case 'SA':
        return 'Super Admin';
      case 'A':
        return 'Admin';
      case 'FSR':
        return 'Field Sales Representative';
      case 'SUPER_DEV_ADMIN':
        return 'Super Dev Admin';
      default:
        return '';
    }
  }
}
